import routes from '@/routes';
import {
  Button,
  Grid,
  PasswordInput,
  Select,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Page } from '@/components';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import validate from '@/helpers/validations';
import { useGetAllRolesMutation, useCreateUserMutation } from '@/services/api';
import { getMutationResult } from '@/helpers/query';

const UserCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, { open, close }] = useDisclosure(false);
  const [Roles, setRoles] = useState();
  const [apiGetAll] = useGetAllRolesMutation();
  const [apiCreateUser] = useCreateUserMutation();

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const { data } = await getMutationResult({}, apiGetAll);
        if (data.success) {
          const mappedRoles = data.data.map((role) => ({
            value: role._id,
            label: role.name,
          }));
          setRoles(mappedRoles);
        } else {
          throw new Error(data.error || 'Failed to load roles');
        }
      } catch (err) {
        throw new Error(err || 'Failed to load roles');
      }
    };

    fetchRoles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const USER_STATUS = [
    { value: 'active', label: t('pages.useredit.status.active') },
    { value: 'blocked', label: t('pages.useredit.status.blocked') },
  ];

  // eslint-disable-next-line consistent-return
  const handleUserCreateClick = async (values) => {
    try {
      open();
      // eslint-disable-next-line no-unused-vars
      const { confirmPassword, ...userValues } = values;
      const newUser = await getMutationResult(userValues, apiCreateUser);

      if (!newUser) {
        notifications.show({
          title: t('pages.usercreate.notification.errortitle'),
          message: t('pages.usercreate.notification.error'),
        });
        close();
        return null;
      }

      notifications.show({
        title: t('pages.usercreate.notification.title'),
        message: t('pages.usercreate.notification.message'),
      });

      navigate(routes.settingsUsers.path);

      close();
    } catch (error) {
      close();
    }
  };

  const form = useForm({
    initialValues: {
      status: 'active',
    },
    validate: {
      password: (value) => validate(value, { required: t('globals.validations.required'), password: t('globals.validations.password') }),
      confirmPassword: (value, values) => (value !== values.password ? t('globals.validations.confirmPassword') : null),
      role: (value) => validate(value, { required: t('globals.validations.required') }),
      status: (value) => validate(value, { required: t('globals.validations.required') }),
      email: (value) => validate(value, {
        required: t('globals.validations.required'),
        email: t('globals.validations.email'),
      }),
    },
  });

  const actions = [
    <Button key="primary" loading={loading} leftSection={<IconCheck />} onClick={form.onSubmit(handleUserCreateClick)}>{t('globals.save')}</Button>,
  ];

  return (
    <Page hideHelp hideNews title={t('pages.usercreate.title')}>
      <form>
        <Grid mb={'xl'}>
          <Grid.Col span={{ base: 6 }}>
            <TextInput
                    autoComplete="off"
                    label={t('pages.usercreate.form.email')}
                    required
                    key={form.key('email')}
                    {...form.getInputProps('email')}
                  />
          </Grid.Col>

          <Grid.Col span={{ base: 6 }}>
            <Select
                    label={t('pages.usercreate.form.role')}
                    data={Roles}
                    required
                    key={form.key('role')}
                    {...form.getInputProps('role')}
                  />
          </Grid.Col>

          <Grid.Col span={{ base: 6 }}>
            <PasswordInput
                    autoComplete="off"
                    label={t('pages.usercreate.form.password')}
                    required
                    key={form.key('password')}
                    {...form.getInputProps('password')}
                  />
          </Grid.Col>

          <Grid.Col span={{ base: 6 }}>
            <PasswordInput
                    autoComplete="off"
                    label={t('pages.usercreate.form.confirmPassword')}
                    required
                    key={form.key('confirmPassword')}
                    {...form.getInputProps('confirmPassword')}
                  />
          </Grid.Col>

          <Grid.Col span={{ base: 6 }}>
            <Select
                    label={t('pages.usercreate.form.status')}
                    data={USER_STATUS}
                    required
                    key={form.key('status')}
                    {...form.getInputProps('status')}
                  />
          </Grid.Col>
        </Grid>

        {actions}
      </form>
    </Page>
  );
};

export default UserCreate;
